import { useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, IconButton } from "@mui/material";
import { FaChevronLeft, FaChevronRight, FaTimes } from "react-icons/fa";

const AdDetails = ({ ad }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === ad.photos.length - 1 ? 0 : prev + 1
    );
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? ad.photos.length - 1 : prev - 1
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{ad?.title}</h1>
        <Link to="/UserAds/Ads" className="text-primary hover:text-primary-dark">
          <FaTimes className="text-2xl" />
        </Link>
      </div>

      {/* Photo Slider */}
      <div className="relative mb-8">
        <div className="relative h-96 overflow-hidden rounded-lg">
          {ad?.photos &&
            ad.photos.map((photo, index) => (
              <img
                key={photo.id}
                src={photo.photo}
                alt={`Ad ${index + 1}`}
                className={`absolute w-full h-full object-cover transition-opacity duration-300 ${
                  index === currentImageIndex ? "opacity-100" : "opacity-0"
                }`}
              />
            ))}
        </div>

        {/* Navigation Arrows */}
        {ad?.photos.length > 1 && (
          <>
            <button
              onClick={handlePrevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/30 text-white p-2 rounded-full hover:bg-black/50"
            >
              <FaChevronLeft className="text-xl" />
            </button>
            <button
              onClick={handleNextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/30 text-white p-2 rounded-full hover:bg-black/50"
            >
              <FaChevronRight className="text-xl" />
            </button>
          </>
        )}

        {/* Image Counter */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
          {currentImageIndex + 1} / {ad?.photos.length}
        </div>

        {/* Zoom Modal Trigger */}
        <button
          onClick={() => setIsModalOpen(true)}
          className="absolute top-4 right-4 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
        >
          <span className="text-sm">View Fullscreen</span>
        </button>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-700">
        <div>
          <h2 className="text-lg font-semibold mb-4">Ad Information</h2>
          <div className="space-y-2">
            <p>
              <span className="font-medium">Description:</span>{" "}
              {ad?.description}
            </p>
            <p>
              <span className="font-medium">Address:</span> {ad?.address}
            </p>
            <p>
              <span className="font-medium">Price:</span>
              {ad?.is_show_price ? ` $${ad.price}` : " Hidden"}
            </p>
            <p>
              <span className="font-medium">Status:</span>
              <span
                className={`ml-2 px-2 py-1 rounded capitalize ${
                  ad?.status === "active"
                    ? "bg-green-100 text-green-800"
                    : ad?.status === "cancel"
                    ? "bg-red-100 text-red-800"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {ad?.status.replace("_", " ")}
              </span>
            </p>
          </div>
        </div>

        <div>
          <h2 className="text-lg font-semibold mb-4">Contact Information</h2>
          <div className="space-y-2">
            <p>
              <span className="font-medium">Phone:</span> {ad?.phone}
            </p>
            <p>
              <span className="font-medium">Email:</span> {ad?.email}
            </p>
            <p>
              <span className="font-medium">Website:</span>
              <a
                href={ad?.site_url}
                className="text-primary hover:underline ml-2"
              >
                {ad?.site_url}
              </a>
            </p>
            <p>
              <span className="font-medium">Video:</span>
              {ad?.video_url && (
                <a
                  href={ad.video_url}
                  className="text-primary hover:underline ml-2"
                >
                  Watch Video
                </a>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Fullscreen Image Modal */}
      {ad?.photos && (
        <Dialog
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          maxWidth="lg"
        >
          <DialogContent className="relative">
            <img
              src={ad.photos[currentImageIndex].photo}
              alt="Fullscreen"
              className="max-h-[80vh] w-auto mx-auto"
            />
            <IconButton
              onClick={() => setIsModalOpen(false)}
              className="!absolute !top-2 !right-2"
            >
              <FaTimes />
            </IconButton>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AdDetails;
